/**
 * Carrusel de promociones de la portada (ADM-05): la lamina grande de arriba,
 * de lado a lado de la pantalla.
 *
 * Es la imagen mas grande de la primera pantalla, o sea el LCP: la primera
 * lamina se carga sin diferir y con sus medidas declaradas, para que la portada
 * no salte cuando baja.
 *
 * Avanza solo, cada seis segundos (rediseño del 14-09-2026, a pedido del
 * cliente). Como este sitio se disena para publico no tecnico, el avance se
 * detiene apenas alguien se apoya en el: con el puntero encima, con el foco del
 * teclado adentro, y con el boton de pausa que queda junto a los puntos. Todo
 * eso lo resuelve `useRotacion`; este componente solo dibuja.
 *
 * Se desliza de lado a lado. Si el sistema pide menos movimiento, la misma
 * pista pasa a fundir una lamina sobre otra (`carrusel.css`): por eso la lamina
 * visible lleva la clase `activa`, que el deslizamiento no necesita.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';

import type { Banner } from '@bandu/compartido';

import { useRotacion } from '../../hooks/use-rotacion';
import { Icono } from '../comunes/Icono';
import { LaminaBanner } from './LaminaBanner';

/** Cuanto se queda cada lamina antes de pasar a la siguiente. */
const INTERVALO_MS = 6000;

export interface CarruselProps {
  banners: Banner[];
}

export function Carrusel({ banners }: CarruselProps) {
  const [detenido, setDetenido] = useState(false);
  const rotacion = useRotacion(banners.length, INTERVALO_MS, detenido);

  if (banners.length === 0) return null;

  const { actual, ir, mover, manejadores } = rotacion;
  const varias = banners.length > 1;

  return (
    <section
      className="carrusel"
      aria-roledescription="carrusel"
      aria-label="Promociones"
      {...manejadores}
    >
      {/* aria-live solo cuando esta detenido: anunciar cada cambio automatico
          seria una interrupcion cada seis segundos para quien usa lector. */}
      <div
        className="carrusel-pista"
        style={{ transform: `translateX(-${actual * 100}%)` }}
        aria-live={detenido || !varias ? 'polite' : 'off'}
      >
        {banners.map((banner, indice) => {
          // La lamina con titulo encima se maqueta distinto en el telefono: ahi
          // la imagen es el fondo del texto y se recorta, mientras que la pieza
          // sin titulo trae la oferta escrita y se muestra entera.
          const conTexto = banner.titulo !== null;
          const clases = [
            'carrusel-lamina',
            conTexto ? 'con-texto' : '',
            banner.imagenMovil !== null ? 'con-movil' : '',
            indice === actual ? 'activa' : '',
          ]
            .filter(Boolean)
            .join(' ');

          // En el telefono, sin pieza propia, la de escritorio se ve entera
          // sobre ella misma ampliada y difuminada (`carrusel.css`).
          const fondo = { '--fondo-lamina': `url("${banner.imagenUrl}")` } as CSSProperties;

          return (
            <div
              key={banner.id}
              className={clases}
              style={fondo}
              role="group"
              aria-roledescription="lamina"
              aria-label={`${indice + 1} de ${banners.length}`}
              aria-hidden={indice !== actual}
              inert={indice !== actual}
            >
              <LaminaBanner banner={banner} prioridad={indice === 0}>
                {conTexto ? (
                  <div className="carrusel-texto sobre-foto">
                    <h2>{banner.titulo}</h2>
                    {banner.bajada !== null ? <p>{banner.bajada}</p> : null}
                  </div>
                ) : null}
              </LaminaBanner>
            </div>
          );
        })}
      </div>

      {varias ? (
        <>
          <button
            type="button"
            className="carrusel-flecha izq"
            onClick={() => mover(-1)}
            aria-label="Promocion anterior"
          >
            <Icono nombre="flechaIzq" />
          </button>
          <button
            type="button"
            className="carrusel-flecha der"
            onClick={() => mover(1)}
            aria-label="Promocion siguiente"
          >
            <Icono nombre="flecha" />
          </button>

          <div className="carrusel-puntos">
            <button
              type="button"
              className="carrusel-pausa"
              onClick={() => setDetenido((valor) => !valor)}
              aria-pressed={detenido}
              aria-label={detenido ? 'Reanudar el carrusel' : 'Detener el carrusel'}
            >
              <Icono nombre={detenido ? 'reproducir' : 'pausa'} />
            </button>
            {banners.map((banner, indice) => (
              <button
                key={banner.id}
                type="button"
                className={indice === actual ? 'carrusel-punto activo' : 'carrusel-punto'}
                onClick={() => ir(indice)}
                aria-label={`Ir a la promocion ${indice + 1}`}
                aria-current={indice === actual}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
